import React, { forwardRef, useCallback, type ReactNode } from 'react';
import { cssMap } from '@atlaskit/css';
import type { PopupComponentProps } from '@atlaskit/popup/types';
import { Popup } from '@atlaskit/popup/popup';
import { token } from '@atlaskit/tokens';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';

const styles = cssMap({
	trigger: {
		display: 'inline-flex',
	},
	caretWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
		paddingRight: token('space.100'),
	},
	caret: {
		width: '16px',
		height: '8px',
		backgroundColor: token('color.background.neutral.bold'),
		clipPath: 'polygon(50% 0, 100% 100%, 0 100%)',
	},
	card: {
		width: '275px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBlockEnd: token('space.150'),
	},
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginBlockStart: token('space.200'),
	},
	dismissButton: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.0'),
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
	},
	okButton: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
		borderColor: token('color.border.inverse'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
	},
});

const TransparentPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
	(
		{
			shouldRenderToParent: _shouldRenderToParent,
			shouldFitContainer: _shouldFitContainer,
			appearance: _appearance,
			isReferenceHidden: _isReferenceHidden,
			shouldFitViewport: _shouldFitViewport,
			children,
			...htmlAttributes
		},
		ref,
	) => (
		<div ref={ref} {...htmlAttributes}>
			{children}
		</div>
	),
);

const POPUP_OFFSET: [number, number] = [0, 8];
const POPUP_FALLBACK_PLACEMENTS: never[] = [];

type Props = {
	isOpen: boolean;
	onDismiss: () => void;
	bodyText: string;
	headingText: string;
	dismissLabel: string;
	primaryActionLabel: string;
	children: ReactNode;
};

type TriggerProps = {
	ref: React.Ref<HTMLDivElement>;
	'aria-controls'?: string;
	'aria-expanded'?: boolean;
	'aria-haspopup'?: boolean | 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid';
};

/**
 * Client-driven spotlight nudge rendered around the global Help button. Unlike
 * the Engagement Platform spotlight (which is server-coordinated), this is shown
 * imperatively in response to a product event, mirroring the Assets pattern.
 */
export const HelpSpotlightPopup = ({
	isOpen,
	onDismiss,
	headingText,
	bodyText,
	dismissLabel,
	primaryActionLabel,
	children,
}: Props): React.JSX.Element => {
	const handleTriggerClickCapture = useCallback(() => {
		if (isOpen) {
			onDismiss();
		}
	}, [isOpen, onDismiss]);

	const renderTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<Box
				ref={triggerProps.ref}
				aria-controls={triggerProps['aria-controls']}
				aria-expanded={triggerProps['aria-expanded']}
				aria-haspopup={triggerProps['aria-haspopup']}
				onClickCapture={handleTriggerClickCapture}
				xcss={styles.trigger}
			>
				{children}
			</Box>
		),
		[children, handleTriggerClickCapture],
	);

	const renderContent = useCallback(
		() => (
			<>
				<Box xcss={styles.caretWrapper}>
					<Box xcss={styles.caret} />
				</Box>
				<Box xcss={styles.card}>
					<Box xcss={styles.header}>
						<Text color="color.text.inverse" size="medium" weight="bold">
							{headingText}
						</Text>
						<Pressable onClick={onDismiss} aria-label={dismissLabel} xcss={styles.dismissButton}>
							<CrossIcon label="" color={token('color.icon.inverse')} />
						</Pressable>
					</Box>
					<Text color="color.text.inverse" size="medium">
						{bodyText}
					</Text>
					<Box xcss={styles.footer}>
						<Pressable onClick={onDismiss} aria-label={primaryActionLabel} xcss={styles.okButton}>
							{primaryActionLabel}
						</Pressable>
					</Box>
				</Box>
			</>
		),
		[bodyText, dismissLabel, headingText, onDismiss, primaryActionLabel],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={onDismiss}
			placement="bottom-end"
			offset={POPUP_OFFSET}
			shouldRenderToParent={false}
			shouldFlip={false}
			shouldFitViewport={false}
			fallbackPlacements={POPUP_FALLBACK_PLACEMENTS}
			popupComponent={TransparentPopupContainer}
			trigger={renderTrigger}
			content={renderContent}
		/>
	);
};
